import React, { useState } from 'react';
import TopBar from '../components/TopBar.jsx';
import {
  LifeBuoy, GraduationCap, MessageSquarePlus, Megaphone, Mail, Phone, Clock, BookOpen, Send,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiPost } from '../lib/api.js';

const TABS = [
  { id: 'contact', label: 'Contact Support', icon: LifeBuoy },
  { id: 'training', label: 'Training', icon: GraduationCap },
  { id: 'feedback', label: 'Feedback', icon: MessageSquarePlus },
  { id: 'updates', label: 'Updates', icon: Megaphone },
];

export default function Support() {
  const [tab, setTab] = useState('contact');
  return (
    <div className="space-y-4">
      <TopBar title="Support" subtitle="Help & Training" />
      <div className="panel p-1 inline-flex gap-1 flex-wrap">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-md text-sm flex items-center gap-2 ${tab === t.id ? 'bg-civix-purple/25 border border-civix-purple/50' : 'text-white/60 hover:text-white'}`}>
              <Icon className="w-4 h-4"/> {t.label}
            </button>
          );
        })}
      </div>
      {tab === 'contact' && <Contact/>}
      {tab === 'training' && <Training/>}
      {tab === 'feedback' && <Feedback/>}
      {tab === 'updates' && <Updates/>}
    </div>
  );
}

function Contact() {
  return (
    <div className="panel p-8 max-w-2xl mx-auto text-center">
      <LifeBuoy className="w-12 h-12 mx-auto text-civix-cyan"/>
      <div className="font-display font-bold text-2xl mt-4">Contact Support</div>
      <p className="text-white/70 mt-2">Specific questions you’d like answered? Our team is here to help.</p>
      <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
        <div className="bg-civix-panelLight rounded-lg p-4">
          <Mail className="w-5 h-5 mx-auto text-civix-cyan"/>
          <div className="text-xs text-white/55 mt-2">Email</div>
          <a href="mailto:support@civix.app" className="text-civix-cyan font-medium">support@civix.app</a>
        </div>
        <div className="bg-civix-panelLight rounded-lg p-4">
          <Phone className="w-5 h-5 mx-auto text-civix-cyan"/>
          <div className="text-xs text-white/55 mt-2">Customer Support Team</div>
          <div className="font-mono">720-764-5467</div>
        </div>
      </div>
      <div className="text-sm text-white/70 mt-6 inline-flex items-center gap-2">
        <Clock className="w-4 h-4"/> Mon–Fri 9am–6pm EST · Sat & Sun closed
      </div>
      <p className="text-xs text-white/55 mt-3">Still need help? Browse the Knowledge Base 24/7 for instant answers.</p>
    </div>
  );
}

function Training() {
  const courses = [
    'Navigation & Header Menu', 'Dashboard Page', 'General Info', 'My Team', 'Power Dialer', 'AI Assistant',
    'Drip Campaigns', 'Lead Vendors', 'Phone Setup', 'Trust Score & 10DLC', 'Recruiting Bootcamp',
  ];
  return (
    <>
      <div className="panel p-8 text-center">
        <BookOpen className="w-12 h-12 mx-auto text-civix-cyan"/>
        <div className="font-display font-bold text-2xl mt-4">Training Center</div>
        <p className="text-white/70 mt-2">View our Knowledge Base to learn more about CIVIX.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        {courses.map((c) => (
          <button key={c} onClick={() => toast.success('Opening course')}
            className="panel p-4 text-left hover:border-civix-purple/50 transition">
            <div className="font-display font-bold">{c}</div>
            <div className="text-xs text-white/55 mt-1">Watch video + reference articles</div>
          </button>
        ))}
      </div>
    </>
  );
}

const TYPE_TO_CATEGORY = {
  Feedback: 'function',
  'Feature Request': 'feature',
  'Bug Report': 'bug',
};

function Feedback() {
  const [type, setType] = useState('Feedback');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    if (!trimmedTitle) { toast.error('Please add a short title.'); return; }
    if (!trimmedBody) { toast.error('Please describe your feedback.'); return; }
    const category = TYPE_TO_CATEGORY[type] || 'function';
    setSubmitting(true);
    try {
      const payload = {
        category,
        title: trimmedTitle.slice(0, 120),
        description: trimmedBody.slice(0, 4000),
        context: {
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
        },
      };
      if (category === 'bug') payload.severity = 'medium';
      const res = await apiPost('/feedback', payload);
      if (res?.ok) {
        toast.success('Feedback submitted');
        setTitle('');
        setBody('');
      } else {
        toast.error(res?.error || 'Could not submit feedback. Please try again.');
      }
    } catch (err) {
      toast.error(err?.message || 'Could not submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="panel p-8 max-w-2xl mx-auto">
      <MessageSquarePlus className="w-12 h-12 mx-auto text-civix-cyan"/>
      <div className="font-display font-bold text-2xl mt-4 text-center">Send Us Feedback or a Feature Request</div>
      <p className="text-white/70 text-sm mt-2 text-center">We review every request during business hours but can’t track each one individually.</p>
      <div className="mt-5 space-y-3">
        <div>
          <label className="label">Select type</label>
          <select className="input mt-1" value={type} onChange={(e) => setType(e.target.value)} disabled={submitting}>
            <option>Feedback</option>
            <option>Feature Request</option>
            <option>Bug Report</option>
          </select>
        </div>
        <div>
          <label className="label">Title</label>
          <input className="input mt-1" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Short summary" disabled={submitting}/>
        </div>
        <div>
          <label className="label">Tell us what we can improve on</label>
          <textarea className="input min-h-40 mt-1" value={body} onChange={(e) => setBody(e.target.value)} maxLength={4000} disabled={submitting}/>
        </div>
        <button
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
          onClick={handleSubmit}
          disabled={submitting}
        >
          <Send className="w-4 h-4"/> {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </div>
  );
}

function Updates() {
  const updates = [
    { date: '2026-04-25', title: 'AI Priority Queue v2', body: 'Power-Dialer now scores leads by intent × recency × value, surfacing the highest-conversion lead first.' },
    { date: '2026-04-18', title: 'Discord Sales Bot', body: 'Live SOLD pings + leaderboard updates posted directly to your team’s Discord channel.' },
    { date: '2026-04-10', title: 'Lead-Vendor universal ingest', body: 'New vendors plug in with a single URL + API key. Auto dedupe, auto TCPA, auto route.' },
    { date: '2026-04-01', title: '10DLC Trust Score', body: 'Track your A2P registration and carrier deliverability per tenant.' },
  ];
  return (
    <div className="space-y-3">
      {updates.map((u) => (
        <div key={u.date} className="panel p-5">
          <div className="text-xs text-civix-purpleSoft uppercase tracking-wider">{u.date}</div>
          <div className="font-display font-bold text-lg mt-1">{u.title}</div>
          <p className="text-sm text-white/70 mt-1">{u.body}</p>
        </div>
      ))}
    </div>
  );
}
